import React, { useEffect, useRef, useState } from 'react';
import './CustomCursor.css';

const CustomCursor: React.FC = () => {
  const cursorRef = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState(false);
  const rafId = useRef<number | null>(null);
  const lastKnownPosition = useRef({ x: 0, y: 0 });
  const shouldUpdate = useRef(false);

  useEffect(() => {
    // Rileva se siamo su mobile
    const checkMobile = () => {
      const isMobileDevice = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) || 
                             window.innerWidth < 768 || 
                             'ontouchstart' in window;
      setIsMobile(isMobileDevice);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const cursor = cursorRef.current;
    if (!cursor || isMobile) return; // Non mostrare il cursore su mobile

    const animate = () => {
      if (shouldUpdate.current) {
        const x = lastKnownPosition.current.x;
        const y = lastKnownPosition.current.y;
        cursor.style.setProperty('--cx', `${x}px`);
        cursor.style.setProperty('--cy', `${y}px`);
        shouldUpdate.current = false;
      }
      rafId.current = requestAnimationFrame(animate);
    };

    rafId.current = requestAnimationFrame(animate);

    // Un unico handler per mousemove che fa tutto il lavoro necessario
    const handleMouseMove = (e: MouseEvent) => {
      // Aggiorna la posizione in un oggetto ref (non causa re-render)
      lastKnownPosition.current = { x: e.clientX, y: e.clientY };
      shouldUpdate.current = true;

      // Gestisce lo stato del cursore video
      const target = e.target as HTMLElement;
      const isModalOpen = document.body.classList.contains('modal-open');
      
      // Non mostrare il cursore play se il modal è aperto
      if (isModalOpen) {
        cursor.classList.remove('video-cursor');
        return;
      }
      
      const isVideo = target.tagName === 'VIDEO' || target.classList.contains('video-item');
      
      if (isVideo) {
        cursor.classList.add('video-cursor');
      } else {
        cursor.classList.remove('video-cursor');
      }
    };

    // Nascondi il cursore quando è fuori dalla finestra
    const handleMouseLeave = () => {
      cursor.style.opacity = '0';
    };

    const handleMouseEnter = () => {
      cursor.style.opacity = '1';
    };

    window.addEventListener('mousemove', handleMouseMove, { passive: true });
    document.addEventListener('mouseleave', handleMouseLeave);
    document.addEventListener('mouseenter', handleMouseEnter);

    return () => {
      if (rafId.current !== null) {
        cancelAnimationFrame(rafId.current);
      }
      window.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseleave', handleMouseLeave);
      document.removeEventListener('mouseenter', handleMouseEnter);
    };
  }, [isMobile]);

  // Non renderizzare il cursore su mobile
  if (isMobile) return null;

  return <div id="custom-cursor" ref={cursorRef} />;
};

export default CustomCursor;

